/**
 * Sprint 3.5 — Página de gestão de releases de firmware.
 *
 * Lista todos os releases já carregados no backend e permite:
 *   - Upload de novo .bin (multipart)
 *   - Deletar release existente (com confirmação)
 *
 * NÃO permite trigger OTA aqui — essa ação está no DeviceDetail, onde
 * o operador tem contexto do device específico (versão atual, secure
 * channel, etc).
 */
import { useCallback, useEffect, useState } from "react";

import {
  deleteFirmwareRelease,
  deprecateFirmwareRelease,
  listFirmwareReleases,
  triggerRollout,
  undeprecateFirmwareRelease,
  uploadFirmware,
} from "../lib/api.js";
import { useCriticalConfirm } from "../components/ConfirmCriticalAction.jsx";
import OtaStatusTable from "../components/OtaStatusTable.jsx";
import { releaseKindBadge, looksLikeBin } from "../lib/fsOta.js";
import { t } from "../i18n/index.js";

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function FirmwareReleases() {
  // S3.10C2 — confirmação forte para rollout de firmware.
  const confirmCritical = useCriticalConfirm();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sprint 3.7 closeout — filtro deprecated
  const [includeDeprecated, setIncludeDeprecated] = useState(false);
  // OTA-FS-2D — filtro por tipo de release: "all" | "app" | "fs".
  const [kindFilter, setKindFilter] = useState("all");

  // Upload form state
  const [uploadVersion, setUploadVersion] = useState("");
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // OTA-FS-2D — tipo do upload: "app" (firmware) | "fs" (LittleFS) + aviso de .bin.
  const [uploadKind, setUploadKind] = useState("app");
  const [fileWarn, setFileWarn] = useState(null);

  const refresh = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        setError(null);
        const data = await listFirmwareReleases({
          include_deprecated: includeDeprecated,
          kind: kindFilter === "all" ? undefined : kindFilter,
          signal,
        });
        setReleases(data?.releases || []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.message || t("firmwareReleases.errLoad"));
      } finally {
        setLoading(false);
      }
    },
    [includeDeprecated, kindFilter]
  );

  useEffect(() => {
    const ctl = new AbortController();
    refresh(ctl.signal);
    return () => ctl.abort();
  }, [refresh]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadVersion || !uploadFile) return;

    try {
      setUploading(true);
      setUploadError(null);
      await uploadFirmware({
        version: uploadVersion.trim(),
        notes: uploadNotes.trim() || undefined,
        file: uploadFile,
        kind: uploadKind,
      });
      // Limpa form + refetch
      setUploadVersion("");
      setUploadNotes("");
      setUploadFile(null);
      setFileWarn(null);
      // Reset do <input type="file"> precisa de ref ou key
      const fileInput = document.getElementById("fw-upload-file");
      if (fileInput) fileInput.value = "";
      await refresh();
    } catch (ex) {
      setUploadError(ex?.message || t("firmwareReleases.errUpload"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (release) => {
    const confirmed = window.confirm(
      t("firmwareReleases.deletePromptTitle", {
        version: release.version,
        sha: release.sha256.slice(0, 8) + "...",
      }) + "\n\n" + t("firmwareReleases.deletePromptBody")
    );
    if (!confirmed) return;
    try {
      await deleteFirmwareRelease(release.id);
      await refresh();
    } catch (ex) {
      setError(ex?.message || t("firmwareReleases.errDelete"));
    }
  };

  // Sprint 3.7 closeout — Deprecate / Undeprecate
  const handleDeprecate = async (release) => {
    const reason = window.prompt(
      t("firmwareReleases.deprecatePromptTitle", { version: release.version }) +
        "\n\n" + t("firmwareReleases.deprecatePromptBody"),
      ""
    );
    if (reason === null) return;
    try {
      await deprecateFirmwareRelease(release.id, { reason });
      await refresh();
    } catch (ex) {
      setError(ex?.message || t("firmwareReleases.errDeprecate"));
    }
  };

  const handleUndeprecate = async (release) => {
    if (
      !window.confirm(
        t("firmwareReleases.undeprecatePromptTitle", { version: release.version }) +
          "\n\n" + t("firmwareReleases.undeprecatePromptBody")
      )
    )
      return;
    try {
      await undeprecateFirmwareRelease(release.id);
      await refresh();
    } catch (ex) {
      setError(ex?.message || t("firmwareReleases.errUndeprecate"));
    }
  };

  // Sprint 3.6 — rollout em massa
  const handleRollout = async (release) => {
    const targetBefore = window.prompt(
      t("firmwareReleases.rolloutPromptVersion", { version: release.version }),
      ""
    );
    if (targetBefore === null) return; // cancelou

    const limitStr = window.prompt(t("firmwareReleases.rolloutPromptLimit"), "10");
    if (limitStr === null) return;
    const limit = parseInt(limitStr, 10);
    if (!Number.isFinite(limit) || limit < 1) {
      window.alert(t("firmwareReleases.rolloutLimitInvalid"));
      return;
    }

    const _secret = await confirmCritical({
      actionLabel: "Rollout da frota (OTA em massa)",
      target: {},
      impact: `Vai enfileirar OTA para a versão ${release.version} em até ${limit} devices do filtro.`,
    });
    if (_secret == null) return;

    try {
      const r = await triggerRollout(release.id, {
        target_version_before: targetBefore,
        limit,
      }, { confirm: _secret });
      const enq = r.enqueued || [];
      const skp = r.skipped || [];
      const reasons = {};
      for (const s of skp) {
        reasons[s.reason] = (reasons[s.reason] || 0) + 1;
      }
      const reasonsStr = Object.entries(reasons)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
      window.alert(
        t("firmwareReleases.rolloutDoneTitle") + "\n\n" +
          t("firmwareReleases.rolloutDoneBody", {
            enq: enq.length,
            skp: skp.length,
          }) +
          (reasonsStr ? t("firmwareReleases.rolloutDoneReasonsPrefix") + reasonsStr + "\n" : "")
      );
    } catch (ex) {
      setError(ex?.message || t("firmwareReleases.errRollout"));
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight mb-1">{t("firmwareReleases.title")}</h1>
        <p className="text-sm text-noc-muted">
          {t("firmwareReleases.subtitle")}
        </p>
      </header>

      {/* Upload form */}
      <section className="card-pad space-y-3">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          {t("firmwareReleases.newRelease")}
        </h2>
        <form onSubmit={handleUpload} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint block mb-1">
                {t("firmwareReleases.version")}
              </label>
              <input
                type="text"
                placeholder={t("firmwareReleases.versionPlaceholder")}
                value={uploadVersion}
                onChange={(e) => setUploadVersion(e.target.value)}
                required
                maxLength={32}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-sm"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint block mb-1">
                {t("firmwareReleases.binFile")}
              </label>
              <input
                id="fw-upload-file"
                type="file"
                accept=".bin,application/octet-stream"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setUploadFile(f);
                  setFileWarn(
                    f && !looksLikeBin(f.name)
                      ? "O arquivo não parece um .bin — confirme que é o binário correto."
                      : null
                  );
                }}
                required
                className="w-full text-sm"
              />
            </div>
            {/* OTA-FS-2D — tipo do release */}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint block mb-1">
                Tipo
              </label>
              <select
                value={uploadKind}
                onChange={(e) => setUploadKind(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 text-sm"
              >
                <option value="app">Firmware app (kind=app)</option>
                <option value="fs">Filesystem / LittleFS (kind=fs)</option>
              </select>
            </div>
          </div>
          {uploadKind === "fs" && (
            <div className="text-[11px] text-noc-accent bg-noc-accent/10 border border-noc-accent/30 rounded p-2">
              Arquivo <span className="font-mono">littlefs.bin</span> (LittleFS), <b>não</b> firmware app.
              Vai para a partição <span className="font-mono">spiffs</span> via <span className="font-mono">fs_update</span>.
            </div>
          )}
          {fileWarn && (
            <div className="text-[11px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
              ⚠ {fileWarn}
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint block mb-1">
              {t("firmwareReleases.notes")}
            </label>
            <textarea
              placeholder={t("firmwareReleases.notesPlaceholder")}
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              maxLength={2000}
              rows={2}
              className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 text-sm"
            />
          </div>
          {uploadError && (
            <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 break-words">
              {uploadError}
            </div>
          )}
          <button
            type="submit"
            disabled={uploading || !uploadVersion || !uploadFile}
            className="px-3 py-1 rounded border border-noc-ok/30 text-noc-ok hover:border-noc-ok/30 disabled:opacity-30 text-sm"
          >
            {uploading ? t("firmwareReleases.uploading") : t("firmwareReleases.upload")}
          </button>
        </form>
      </section>

      {/* Lista de releases */}
      <section className="card-pad space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
            {t("firmwareReleases.releasesCount", { n: releases.length })}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            {/* OTA-FS-2D — filtro por tipo */}
            <div className="flex items-center rounded border border-noc-border overflow-hidden">
              {[
                { k: "all", label: "Todos" },
                { k: "app", label: "App" },
                { k: "fs", label: "Filesystem" },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setKindFilter(o.k)}
                  className={
                    "text-xs px-2 py-1 " +
                    (kindFilter === o.k
                      ? "bg-noc-accent/15 text-noc-accent font-semibold"
                      : "text-noc-muted hover:text-noc-text")
                  }
                >
                  {o.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1 text-xs text-noc-muted cursor-pointer">
              <input
                type="checkbox"
                checked={includeDeprecated}
                onChange={(e) => setIncludeDeprecated(e.target.checked)}
                className="cursor-pointer"
              />
              {t("firmwareReleases.showDeprecated")}
            </label>
            <button
              type="button"
              onClick={() => refresh()}
              disabled={loading}
              className="text-xs px-2 py-1 rounded border border-noc-muted/40 hover:border-noc-text/60 disabled:opacity-50"
            >
              🔄
            </button>
          </div>
        </div>

        {error && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 break-words">
            {error}
          </div>
        )}

        {loading && releases.length === 0 ? (
          <div className="text-sm text-noc-muted">{t("common.loading")}</div>
        ) : releases.length === 0 ? (
          <div className="text-sm text-noc-muted">
            {t("firmwareReleases.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint border-b border-noc-border">
                  <th className="py-2 pr-3">{t("firmwareReleases.colVersion")}</th>
                  <th className="py-2 pr-3">{t("firmwareReleases.colStatus")}</th>
                  <th className="py-2 pr-3">{t("firmwareReleases.colSha")}</th>
                  <th className="py-2 pr-3">{t("firmwareReleases.colSize")}</th>
                  <th className="py-2 pr-3">{t("firmwareReleases.colCreated")}</th>
                  <th className="py-2 pr-3">{t("firmwareReleases.colNotes")}</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {releases.map((r) => (
                  <tr
                    key={r.id}
                    className={
                      "border-b border-noc-border/30 hover:bg-noc-bg/40 " +
                      (r.deprecated ? "opacity-60" : "")
                    }
                  >
                    <td className="py-2 pr-3 font-mono">
                      <span className="inline-flex items-center gap-1.5">
                        {r.version}
                        {(() => {
                          const b = releaseKindBadge(r);
                          return (
                            <span
                              title={b.title}
                              className={
                                "text-[9px] font-mono px-1 py-0.5 rounded border " +
                                (b.tone === "fs"
                                  ? "border-noc-accent/40 text-noc-accent"
                                  : "border-noc-border text-noc-muted")
                              }
                            >
                              {b.label}
                            </span>
                          );
                        })()}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      {r.deprecated ? (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded bg-noc-warn/10 border border-noc-warn/30 text-noc-warn font-mono"
                          title={
                            r.deprecated_reason
                              ? `${r.deprecated_reason}\n${formatDate(r.deprecated_at)}`
                              : `${formatDate(r.deprecated_at)}`
                          }
                        >
                          {t("firmwareReleases.statusDeprecated")}
                        </span>
                      ) : (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-noc-ok/10 border border-noc-ok/30 text-noc-ok font-mono">
                          {t("firmwareReleases.statusActive")}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      <span title={r.sha256} className="cursor-help">
                        {r.sha256.slice(0, 16)}…
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-mono">
                      {formatBytes(r.size_bytes)}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="py-2 pr-3 text-xs text-noc-muted">
                      {r.notes || "—"}
                    </td>
                    <td className="py-2">
                      <div className="flex gap-1 items-center flex-wrap">
                        {!r.deprecated && (
                          <button
                            type="button"
                            onClick={() => handleRollout(r)}
                            className="text-xs px-2 py-1 rounded border border-noc-ok/30 text-noc-ok hover:border-noc-ok/30"
                            title={t("firmwareReleases.btnRolloutTitle")}
                          >
                            {t("firmwareReleases.btnRollout")}
                          </button>
                        )}
                        {r.deprecated ? (
                          <button
                            type="button"
                            onClick={() => handleUndeprecate(r)}
                            className="text-xs px-2 py-1 rounded border border-noc-ok/30 text-noc-ok hover:border-noc-ok/30"
                            title={t("firmwareReleases.btnUndeprecateTitle")}
                          >
                            {t("firmwareReleases.btnUndeprecate")}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleDeprecate(r)}
                            className="text-xs px-2 py-1 rounded border border-noc-warn/30 text-noc-warn hover:border-noc-warn/30"
                            title={t("firmwareReleases.btnDeprecateTitle")}
                          >
                            {t("firmwareReleases.btnDeprecate")}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDelete(r)}
                          className="text-xs px-2 py-1 rounded border border-noc-err/30 text-noc-err hover:border-noc-err/30"
                          title={t("firmwareReleases.btnDeleteTitle")}
                        >
                          {t("firmwareReleases.btnDelete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Sprint 3.6 — tabela detalhada OTA status por device */}
      <OtaStatusTable />
    </div>
  );
}
